import { isUndefined } from 'lodash'
import { getIdOrNull, getMatrix, isObject } from '../../../utils'
import { SCHEDULE_VALIDATED } from '../../../utils/entities/internship'
import { MONTH_STRING_FORMAT, WEEK_NUMBER_FORMAT } from '../../../utils/momentjs'
import { PeriodTypes } from '../../../utils/timePeriods'
import { Types } from '../../antd/Tables/DragTable/ResizingDiv'
import { getXCoordinates, getYCoordinates } from '../../antd/Tables/utils'
import { getLaneKey, INCLUSION_LINE_KEY, LaneTypes } from '../Quotas/utils'

export const getInternshipsPeriods = ({ startDate, type, quantity }, translator) => {
  const periods = {
    months: [],
    weeks: []
  }
  const step = PeriodTypes.WEEK

  let iterationDate = startDate.clone()
  let currentMonth = {
    month: iterationDate.month(),
    label: translator(iterationDate.format(MONTH_STRING_FORMAT)),
    weeks: []
  }

  for (let iStep = 0; iStep < quantity; iStep++) {
    currentMonth.weeks.push({
      startDate: iterationDate.clone().startOf(step),
      endDate: iterationDate.clone().endOf(step),
      number: iterationDate.format(WEEK_NUMBER_FORMAT)
    })

    periods.weeks.push(iterationDate.clone())
    iterationDate = iterationDate.clone().add(1, step)

    if (iterationDate.month() !== currentMonth.month) {
      periods.months.push(currentMonth)

      currentMonth = {
        month: iterationDate.month(),
        label: translator(iterationDate.format(MONTH_STRING_FORMAT)),
        weeks: []
      }
    }
  }

  if (currentMonth.weeks.length > 0) {
    periods.months.push(currentMonth)
  }

  return periods
}

const formatInternship = (weeks, matrix, sectionGroupsMap, getValue, getConfig) => internship => {
  const sectionId = isObject(internship.section) && isObject(internship.section.sectionToYear) ? getIdOrNull(internship.section.sectionToYear.section) : -1

  const laneConfig = sectionGroupsMap[sectionId] ? { type: LaneTypes.GROUP, id: sectionGroupsMap[sectionId] } : { type: LaneTypes.SECTION, id: sectionId }
  const laneKey = getLaneKey(laneConfig.type, laneConfig)

  const key = isUndefined(matrix[laneKey]) ? INCLUSION_LINE_KEY : laneKey

  const x = getXCoordinates(internship, weeks)

  return ({
    id: internship.id,
    key,
    x,
    value: getValue(internship),
    config: getConfig(internship),
    type: internship.state === SCHEDULE_VALIDATED ? Types.CLEAR : Types.SOLID,
    internship
  })
}

const sortByImportance = (a, b) => {
  const aPrio = a.internship.isException ? -1 : 1
  const bPrio = b.internship.isException ? -1 : 1

  if (bPrio - aPrio !== 0) {
    return bPrio - aPrio
  }

  const startPrio = a.x[0] - b.x[0]

  if (startPrio !== 0) {
    return startPrio
  }

  const aLength = a.x[1] - a.x[0]
  const bLength = b.x[1] - b.x[0]

  return bLength - aLength
}

export const getInternshipsMatrix = (lines, weeks, sectionGroups, getConfig, getValue) => {
  const internshipsAcc = lines.reduce((acc, line) => {
    acc.data[line.id] = []
    acc.matrix[line.id] = getMatrix(weeks.length, 1)
    acc.places[line.id] = line.places

    return acc
  }, { data: {}, matrix: {}, places: {} })

  const sectionGroupsMap = sectionGroups.reduce((acc, group) => {
    group.sections.forEach(section => {
      acc[getIdOrNull(section)] = group.id
    })

    return acc
  }, {})

  lines.forEach(line => {
    line.internships
      .map(formatInternship(weeks, internshipsAcc.matrix, sectionGroupsMap, getValue, getConfig))
      .sort(sortByImportance)
      .forEach(data => {
        const key = data.key
        const x = data.x

        const isOverflow = key === INCLUSION_LINE_KEY && internshipsAcc.places[key] === 0
        const matrix = internshipsAcc.matrix[key] && internshipsAcc.matrix[key].length > 0 ? internshipsAcc.matrix[key] : getMatrix(weeks.length, 1)
        let y = getYCoordinates({ total: 1 }, matrix, x, data.internship.isException ? line.places : 0)

        if (isOverflow && y[0] === 0) {
          y = y.map(coord => ++coord)
        }

        /** @TODO improve algorithm to complete and fill in only one action */
        if (y[1] >= internshipsAcc.matrix[key].length) {
          for (let i = internshipsAcc.matrix[key].length; i <= y[1]; i++) {
            internshipsAcc.matrix[key].push(new Array(weeks.length).fill(0))
          }
        }

        const width = x[1] >= weeks.length ? weeks.length - 1 : x[1]

        for (let i = y[0]; i <= y[1]; i++) {
          for (let j = x[0]; j <= width; j++) {
            internshipsAcc.matrix[key][i][j] = 1
          }
        }

        data.y = y

        internshipsAcc.data[key].push(data)
      })
  })

  const overflows = lines.reduce((acc, line) => {
    const overflow = internshipsAcc.matrix[line.id].length - Math.max(1, line.places)

    acc[line.id] = Math.max(overflow, 0)
    return acc
  }, {})

  return { data: internshipsAcc.data, overflows }
}

export const reduceBySector = (data, sectors = []) => {
  const seedMap = sectors.reduce((acc, sector) => {
    acc[getIdOrNull(sector) ?? -1] = []

    return acc
  }, {})

  return data.reduce((acc, d) => {
    const key = getIdOrNull(d.sector) ?? -1

    if (!acc[key]) {
      acc[key] = []
    }

    acc[key].push(d)

    return acc
  }, seedMap)
}

export const getDifference = (a, b) => {
  return a.filter(x => !b.some(y => x.id === y.id))
}

export const getDivStyle = ({ background, border, text }) => ({
  backgroundColor: background,
  borderColor: border,
  color: text
})
